import { useEffect, useMemo, useState } from 'react';
import { format, getISODay, parseISO } from 'date-fns';
import { supabase } from '../lib/supabase';
import { formatTime } from '../lib/format';

// Shared "pick a class" control used by both the Clients tab's "+ Add to
// class" and the Inquiries tab's "Add to class" — a dropdown of active
// classes (with spots left) that, once one is picked, shows who's
// already enrolled so staff can sanity-check age/breed mix before adding.
const MAX_CLASS_SIZE = 5;

interface ClassOption {
  id: string;
  title: string | null;
  start_at: string;
  class_types: { name: string } | null;
  instructors: { name: string } | null;
  enrollments: { count: number }[];
}

interface RosterPreviewRow {
  client_id: string;
  clients: {
    first_name: string;
    last_name: string;
    dog_name: string | null;
    breed: string | null;
    age: number | null;
  } | null;
}

export function ClassPicker({
  value,
  onChange,
  excludeClassIds,
}: {
  value: string;
  onChange: (classId: string) => void;
  excludeClassIds?: Set<string>;
}) {
  const [classes, setClasses] = useState<ClassOption[] | null>(null);
  const [roster, setRoster] = useState<RosterPreviewRow[] | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('classes')
        .select('id, title, start_at, class_types(name), instructors(name), enrollments(count)')
        .eq('status', 'active')
        .order('start_at');
      setClasses((data ?? []) as unknown as ClassOption[]);
    })();
  }, []);

  const available = useMemo(() => {
    if (!classes) return [];
    return [...classes]
      .filter((c) => !excludeClassIds?.has(c.id))
      .sort((a, b) => {
        const da = parseISO(a.start_at);
        const db = parseISO(b.start_at);
        const dayCmp = getISODay(da) - getISODay(db);
        if (dayCmp !== 0) return dayCmp;
        return format(da, 'HH:mm').localeCompare(format(db, 'HH:mm'));
      });
  }, [classes, excludeClassIds]);

  useEffect(() => {
    if (!value) {
      setRoster(null);
      return;
    }
    let cancelled = false;
    setRoster(null);
    (async () => {
      const { data } = await supabase
        .from('enrollments')
        .select('client_id, clients(first_name, last_name, dog_name, breed, age)')
        .eq('class_id', value);
      if (cancelled) return;
      setRoster((data ?? []) as unknown as RosterPreviewRow[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [value]);

  return (
    <div className="field">
      <label htmlFor="class-picker">Class</label>
      {classes === null ? (
        <p className="muted">Loading classes…</p>
      ) : available.length === 0 ? (
        <p className="muted" style={{ marginTop: 0 }}>
          No active classes available.
        </p>
      ) : (
        <select
          id="class-picker"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          autoFocus
        >
          <option value="" disabled>
            Choose a class…
          </option>
          {available.map((c) => {
            const enrolled = c.enrollments[0]?.count ?? 0;
            const spotsLeft = Math.max(0, MAX_CLASS_SIZE - enrolled);
            return (
              <option key={c.id} value={c.id} disabled={spotsLeft === 0}>
                {format(parseISO(c.start_at), 'EEE')} · {formatTime(c.start_at)} —{' '}
                {c.title || c.class_types?.name || 'Class'} ({c.instructors?.name ?? '—'}) —{' '}
                {spotsLeft === 0 ? 'Full' : `${spotsLeft} spot${spotsLeft === 1 ? '' : 's'} left`}
              </option>
            );
          })}
        </select>
      )}

      {value && (
        <div style={{ marginTop: '0.7rem' }}>
          <p className="muted small" style={{ margin: '0 0 0.3rem' }}>
            Currently enrolled:
          </p>
          {roster === null ? (
            <p className="muted small" style={{ margin: 0 }}>
              Loading…
            </p>
          ) : roster.length === 0 ? (
            <p className="muted small" style={{ margin: 0 }}>
              No one enrolled yet.
            </p>
          ) : (
            <ul className="muted small" style={{ margin: 0, paddingLeft: '1.1rem' }}>
              {roster.map((r) => (
                <li key={r.client_id}>
                  {r.clients ? `${r.clients.first_name} ${r.clients.last_name}` : 'Client'}
                  {r.clients?.dog_name ? ` — ${r.clients.dog_name}` : ''}
                  {r.clients?.breed ? `, ${r.clients.breed}` : ''}
                  {r.clients?.age != null ? `, ${r.clients.age}y` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
